import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RuleIcon from "@mui/icons-material/Rule";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import Swal from "sweetalert2";
import Layout from "../../components/layout/Layout";
import { nowMealsApi } from "../../api/nowMeals";
import { itemCategoriesApi } from "../../api/itemCategories";
import DailyAvailabilityTab from "./DailyAvailabilityTab";
import AvailabilityRulesTab from "./AvailabilityRulesTab";
import AutomaticStockTab from "./AutomaticStockTab";

interface NowMeal {
  _id: string;
  name: string;
  image: string;
  categoryId: { _id: string; name: string } | null;
  currentPrice: number;
  mrp: number;
  isActive: boolean;
  isAddOn?: boolean;
  displayOrder?: number;
  cartAddCount?: number;
  cartRemoveCount?: number;
}

interface Category {
  _id: string;
  name: string;
}

const NowMeals = () => {
  const { admin } = useAuth();
  const canManageCatalogue = admin?.role === "super-admin";
  const canReadAutomaticStock = Boolean(admin && ((admin.permissions?.nowMeals ?? ["super-admin", "admin", "manager"].includes(admin.role)) || (admin.permissions?.ops ?? ["super-admin", "admin", "manager", "kitchen-staff"].includes(admin.role))));
  const canEditPrices = admin?.role === "admin" || admin?.role === "super-admin";
  const navigate = useNavigate();
  const [meals, setMeals] = useState<NowMeal[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [catalogueStatus, setCatalogueStatus] = useState<"all" | "active" | "inactive">("all");
  const [kind, setKind] = useState<"all" | "meal" | "addon">("all");
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchDependencies = async () => {
    try {
      const response = await itemCategoriesApi.getCategories();
      setCategories(response.categories || []);
    } catch (fetchError) {
      console.error("Failed to fetch instant meal categories", fetchError);
    }
  };

  const fetchMeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await nowMealsApi.getNowMeals({
        page,
        limit: 12,
        search: debouncedSearch || undefined,
        categoryId: categoryId || undefined,
        isActive: catalogueStatus === "all" ? undefined : catalogueStatus === "active",
        isAddOn: kind === "all" ? undefined : kind === "addon",
        sortBy: "displayOrder",
        sortOrder: "asc",
      });

      const nextMeals = data.nowMeals || [];
      setMeals(nextMeals);
      setTotalPages(data.pagination?.pages || 1);
    } catch (fetchError) {
      console.error("Failed to fetch meals", fetchError);
      setError("Could not fetch Instant Meals");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, categoryId, catalogueStatus, kind]);

  useEffect(() => {
    if (canManageCatalogue) void fetchDependencies();
  }, [canManageCatalogue]);

  useEffect(() => {
    if (canManageCatalogue && activeTab === 2) void fetchMeals();
  }, [fetchMeals, canManageCatalogue, activeTab]);

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete instant meal?",
      text: "This will remove the item permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#334155",
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await nowMealsApi.deleteNowMeal(id);
      await fetchMeals();
      Swal.fire("Deleted", "Instant meal removed successfully.", "success");
    } catch (deleteError) {
      Swal.fire("Error", "Failed to delete instant meal", "error");
    }
  };

  const handleResetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setCategoryId("");
    setCatalogueStatus("all");
    setKind("all");
    setPage(1);
  };

  return (
    <Layout>
      <Box sx={{ minWidth: 0 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} color="#0f172a" sx={{ fontSize: { xs: 26, sm: 34 } }}>
              Instant Meals
            </Typography>
          </Box>
          {canManageCatalogue && activeTab === 2 && <Stack direction="row" spacing={1.5} sx={{ width: { xs: "100%", lg: "auto" }, "& .MuiButton-root": { flex: { xs: 1, lg: "initial" }, minWidth: 0 } }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => void fetchMeals()}
              sx={{ borderRadius: 999 }}
            >
              Refresh
            </Button>
            {activeTab === 2 && canEditPrices && <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/now-meals/create")}
              sx={{
                borderRadius: 999,
                background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
              }}
            >
              Add Instant Meal
            </Button>}
          </Stack>}
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3, minWidth: 0 }}>
          <Tabs
            value={canManageCatalogue || (canReadAutomaticStock && activeTab === 3) ? activeTab : 0}
            onChange={(_, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                minHeight: 48,
                maxWidth: { xs: 230, sm: 360 },
                whiteSpace: "normal",
                "& .MuiTab-iconWrapper": { flexShrink: 0 },
              },
            }}
          >
            <Tab value={0} icon={<CalendarTodayIcon />} iconPosition="start" label="Daily Availability & Menu" />
            {canManageCatalogue && <Tab value={1} icon={<RuleIcon />} iconPosition="start" label="Recurring Availability Rules" />}
            {canManageCatalogue && <Tab value={2} icon={<RestaurantMenuIcon />} iconPosition="start" label="Meal Catalog" />}
            {canReadAutomaticStock && <Tab value={3} icon={<RuleIcon />} iconPosition="start" label="Automatic Stock" />}
          </Tabs>
        </Box>

        {activeTab === 0 && <DailyAvailabilityTab />}
        {canReadAutomaticStock && activeTab === 3 && <AutomaticStockTab />}
        {canManageCatalogue && activeTab === 1 && <AvailabilityRulesTab />}
        {canManageCatalogue && activeTab === 2 && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Card sx={{ mb: 3, borderRadius: 4, boxShadow: "0 18px 40px rgba(15,23,42,0.08)" }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Search meals"
                placeholder="Name or description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: { xs: 0, md: 220 }, width: { xs: "100%", md: "auto" } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryId}
                  label="Category"
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: 0, md: 180 }, width: { xs: "100%", md: "auto" } }}>
                <InputLabel>Catalogue Status</InputLabel>
                <Select
                  value={catalogueStatus}
                  label="Catalogue Status"
                  onChange={(e) => {
                    setCatalogueStatus(e.target.value as typeof catalogueStatus);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: 0, md: 160 }, width: { xs: "100%", md: "auto" } }}>
                <InputLabel>Kind</InputLabel>
                <Select
                  value={kind}
                  label="Kind"
                  onChange={(e) => {
                    setKind(e.target.value as typeof kind);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All items</MenuItem>
                  <MenuItem value="meal">Meals</MenuItem>
                  <MenuItem value="addon">Add-ons</MenuItem>
                </Select>
              </FormControl>
              <Button variant="text" onClick={handleResetFilters}>
                Reset
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 18px 40px rgba(15,23,42,0.08)" }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table sx={{ minWidth: 760 }}>
                    <TableHead>
                      <TableRow sx={{ background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Meal</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Popularity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {meals.map((meal) => {
                        return (
                          <TableRow key={meal._id} hover>
                            <TableCell>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                  src={meal.image}
                                  variant="rounded"
                                  sx={{ width: 56, height: 56, borderRadius: 3 }}
                                />
                                <Box>
                                  <Typography fontWeight={700} color="#0f172a">
                                    {meal.name}
                                  </Typography>
                                  <Stack direction="row" spacing={1} mt={0.5}>
                                    <Chip
                                      size="small"
                                      label={meal.isAddOn ? "Add-on" : "Meal"}
                                      sx={{
                                        backgroundColor: meal.isAddOn ? "#ede9fe" : "#dbeafe",
                                        color: meal.isAddOn ? "#6d28d9" : "#1d4ed8",
                                      }}
                                    />
                                    {meal.displayOrder !== undefined && (
                                      <Chip size="small" variant="outlined" label={`Order ${meal.displayOrder}`} />
                                    )}
                                  </Stack>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip label={meal.categoryId?.name || "Uncategorized"} variant="outlined" />
                            </TableCell>
                             <TableCell>
                              <Typography fontWeight={700}>Rs {meal.currentPrice}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                MRP Rs {meal.mrp}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={600} color="success.main" variant="body2">
                                + {meal.cartAddCount || 0} Adds
                              </Typography>
                              <Typography fontWeight={600} color="error.main" variant="body2">
                                - {meal.cartRemoveCount || 0} Removes
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Chip
                                  size="small"
                                  label={meal.isActive ? "Active" : "Inactive"}
                                  variant="outlined"
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit">
                                <IconButton
                                  color="primary"
                                  onClick={() => navigate(`/now-meals/${meal._id}/edit`)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton color="error" onClick={() => void handleDelete(meal._id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                       {meals.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No instant meals matched these filters
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Try clearing filters or create a new item.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box display="flex" justifyContent="center" py={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color="primary"
                      shape="rounded"
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default NowMeals;
